"use client";

import { useState } from "react";
import { useTranslations } from "use-intl";
import z from "zod";

import { MailIcon } from "@/components/appIcons";
import { useAppForm } from "@/components/forms/app-form";
import { Link } from "@/components/link";
import { authClient } from "@/lib/auth-client";

import { Button } from "./ui/button";

export default function ForgotPasswordForm() {
  const t = useTranslations("globals.auth");
  const [sent, setSent] = useState(false);
  const forgotPasswordSchema = z.object({
    email: z.email(t("invalidEmail")),
  });

  const form = useAppForm({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value }) => {
      const input = forgotPasswordSchema.parse(value);
      const result = await authClient.requestPasswordReset({
        email: input.email,
        redirectTo: "/reset-password",
      });

      if (result.error) {
        return;
      }

      setSent(true);
    },
    validators: {
      onSubmit: forgotPasswordSchema,
    },
  });

  if (sent) {
    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex items-center justify-center size-16 rounded-full bg-primary/10">
            <MailIcon className="text-3xl text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">{t("resetLinkSent")}</h1>
          <p className="text-sm text-muted-foreground">{t("resetLinkSentDescription")}</p>
        </div>
        <Link to="/login" rel="nofollow">
          <Button variant="outline" className="mt-4">
            {t("backToSignIn")}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">{t("forgotPasswordTitle")}</h1>
        <p className="text-sm text-muted-foreground">{t("forgotPasswordDescription")}</p>
      </div>

      <form.AppForm>
        <form.Form className="space-y-4">
          <form.AppField name="email">
            {(field) => <field.TextField label={t("email")} type="email" />}
          </form.AppField>

          <form.SubmitButton className="w-full" size="lg" submittingLabel={t("submitting")}>
            {t("sendResetLink")}
          </form.SubmitButton>
        </form.Form>
      </form.AppForm>

      <p className="text-center text-sm text-muted-foreground">
        <Link to="/login" rel="nofollow" className="text-primary hover:underline font-medium">
          {t("backToSignIn")}
        </Link>
      </p>
    </div>
  );
}
